import { useEffect, useState } from 'react';
import type { PropertyResponse } from '@/shared/api';
import { availabilityState } from './AvailabilityCard';
import { buildManualBookingRequest, nightsBetween, plusDaysIso, todayIso } from './helpers';
import { canSubmitManualBooking, EMPTY_GUEST, type GuestFormValues } from './manualBookingRules';
import { useAvailability } from './useAvailability';
import { useManualBooking } from './useManualBooking';

export type PanelApartment =
  | { property: Pick<PropertyResponse, 'id' | 'name' | 'adminDisplayName' | 'currency'> }
  | { choices: PropertyResponse[] };

/** Zustand der manuellen Buchung: Eingaben, Prüfung, Anlegen und was daraus folgt. */
export function useManualBookingForm(args: {
  open: boolean;
  apartment: PanelApartment;
  prefillFrom?: string;
  prefillTo?: string;
  onCreated: () => void;
}) {
  const { open, apartment, prefillFrom, prefillTo } = args;
  const choices = 'choices' in apartment ? apartment.choices : null;
  const [chosenId, setChosenId] = useState('');
  const property =
    'property' in apartment ? apartment.property : choices?.find((p) => p.id === chosenId);
  const [from, setFrom] = useState(prefillFrom ?? todayIso);
  const [to, setTo] = useState(() => prefillTo ?? plusDaysIso(prefillFrom ?? todayIso(), 2));
  const [guest, setGuest] = useState<GuestFormValues>(EMPTY_GUEST);
  const [override, setOverride] = useState(false);

  // Jedes Öffnen beginnt leer: vorher standen die Felder der letzten Buchung noch da.
  useEffect(() => {
    if (!open) return;
    setFrom(prefillFrom ?? todayIso());
    setTo(prefillTo ?? plusDaysIso(prefillFrom ?? todayIso(), 2));
    setGuest(EMPTY_GUEST);
    setOverride(false);
    setChosenId('');
  }, [open, prefillFrom, prefillTo]);

  const nights = nightsBetween(from, to);
  const rate = Number(guest.nightlyRate) || 0;
  const datesValid = nights > 0;
  const propertyId = property?.id ?? '';
  const avail = useAvailability({
    propertyId,
    from,
    to,
    enabled: open && datesValid && Boolean(property),
  });
  const create = useManualBooking({ onSuccess: args.onCreated });
  // Eine Bestätigung gilt nur für die Hinweise, die gerade da standen.
  useEffect(() => setOverride(false), [propertyId, from, to]);
  const state = availabilityState({
    loading: avail.isLoading,
    unavailable: avail.isUnavailable,
    hard: avail.hardConflicts.length,
    soft: avail.softConflicts.length,
  });
  // Ohne Antwort der Prüfung wird nicht angelegt: Mindestnächte und Sperren kennt nur sie.
  const canSubmit =
    Boolean(property) &&
    avail.data !== undefined &&
    canSubmitManualBooking({
      datesValid,
      guest,
      rate,
      availabilityUnknown: avail.isUnavailable,
      hardConflicts: avail.hardConflicts,
      softConflicts: avail.softConflicts,
      override,
    });
  return {
    choices,
    property,
    propertyId,
    setChosenId,
    from,
    to,
    setFrom,
    setTo,
    guest,
    setField: <K extends keyof GuestFormValues>(field: K, value: GuestFormValues[K]) =>
      setGuest((current) => ({ ...current, [field]: value })),
    override,
    setOverride,
    nights,
    rate,
    datesValid,
    avail,
    create,
    state,
    canSubmit,
    /** Etwas eingegeben, das beim Schließen verloren ginge. */
    dirty: chosenId !== '' || JSON.stringify(guest) !== JSON.stringify(EMPTY_GUEST),
    submit: () =>
      create.mutate(buildManualBookingRequest({ propertyId, from, to, guest, nightlyRate: rate })),
  };
}
